import { useReveal } from '../hooks/useReveal.js';
import { preferredStoreUrl } from '@/lib/appStores.js';

// Pre-rendered quote-card artwork shipped with the app (the "Quotes of the
// Saints" feature). Served from the web app's own /quotes folder.
const cards: Array<{ slug: string; saint: string }> = [
  { slug: 'st-paisios-of-mount-athos-01', saint: 'St Paisios of Mount Athos' },
  { slug: 'st-isaac-the-syrian-01', saint: 'St Isaac the Syrian' },
  { slug: 'st-porphyrios-01', saint: 'St Porphyrios' },
  { slug: 'st-john-chrysostom-01', saint: 'St John Chrysostom' },
  { slug: 'st-sophrony-of-essex-01', saint: 'St Sophrony of Essex' },
  { slug: 'st-nektarios-of-aegina-01', saint: 'St Nektarios of Aegina' },
  { slug: 'st-ignatius-brianchaninov-01', saint: 'St Ignatius Brianchaninov' },
  { slug: 'st-joseph-the-hesychast-01', saint: 'St Joseph the Hesychast' },
  { slug: 'st-john-of-the-ladder-01', saint: 'St John of the Ladder' },
  { slug: 'st-basil-the-great-01', saint: 'St Basil the Great' },
  { slug: 'abba-macarius-the-great-01', saint: 'Abba Macarius the Great' },
  { slug: 'patriarch-pavle-of-serbia-01', saint: 'Patriarch Pavle of Serbia' },
];

function QuoteCard({ slug, saint }: { slug: string; saint: string }) {
  return (
    <figure className="ow-quote-card" aria-label={`A saying of ${saint}`}>
      <picture>
        <source srcSet={`/quotes/${slug}.webp`} type="image/webp" />
        <img
          src={`/quotes/${slug}.jpg`}
          alt={`A saying of ${saint}`}
          loading="lazy"
          decoding="async"
          width={320}
          height={480}
        />
      </picture>
    </figure>
  );
}

export function SacredWords() {
  const headerRef = useReveal<HTMLDivElement>(0.3);
  // Render the list twice so the marquee loops seamlessly.
  const loop = [...cards, ...cards];
  return (
    <section
      id="sacred-words"
      className="ow-sacred-words relative w-full overflow-hidden"
      style={{ padding: '84px 0 56px' }}
    >
      <div className="mx-auto max-w-container px-5 md:px-8">
        <div ref={headerRef} className="ow-reveal text-center max-w-[760px] mx-auto">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            Words of the Saints
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            A saying to carry through the day.
          </h2>
          <p className="scripture text-secondary mt-4" style={{ fontSize: 20, lineHeight: 1.5 }}>
            Every morning, a card from the Fathers, to read, to keep, to share.
          </p>
        </div>
      </div>

      {/* Auto-scrolling film strip of quote cards. */}
      <div className="ow-quote-marquee mt-16" role="group" aria-label="Quotes of the saints">
        <div className="ow-quote-track">
          {loop.map((c, i) => (
            <QuoteCard key={`${c.slug}-${i}`} slug={c.slug} saint={c.saint} />
          ))}
        </div>
      </div>

      <div className="text-center mt-14 px-5">
        <a
          href={preferredStoreUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="ow-cta-ghost"
        >
          A new quote each day
        </a>
      </div>
    </section>
  );
}
